import {
  Building2,
  Database,
  FileText,
  Key,
  Laptop,
  Monitor,
  Network,
  Scale,
  Shield
} from 'lucide-react';

export const COMPLIANCE_DATA = {
  subtitle: 'SOC 2',
  icon: <Shield />,
  author: 'CBIZ LLP (CBIZ)',
  description:
    "Foundry has successfully completed a System and Organization Controls (SOC) 2 Type II audit, performed by CBIZ LLP (CBIZ). Developed by the \
    American Institute of Certified Public Accountants (AICPA), the SOC 2 information security audit provides a report on the examination of controls relevant to \
    the trust services criteria categories covering security, availability, processing integrity, confidentiality, and privacy. A SOC 2 Type II report describes a \
    service organization's systems, whether the design of specified controls meets the relevant trust services categories, and assesses the effectiveness of those \
    controls over a specified period of time. Foundry's SOC 2 Type II report did not have any noted exceptions and was therefore issued with a \"clean\" audit \
    opinion from CBIZ.\n \
    \nTo request access to Foundry's report, please go to Trust Center > Audit Reports when logged into your Foundry Pool account, or email risk@foundrydigital.com \
    and work with your relationship manager to obtain the report."
};

export const SECURITY_CONTROL_ITEMS = {
  dataSecurity: {
    subtitle: '',
    icon: <Database />,
    author: null,
    summaryDetails: [
      'Access Monitoring',
      'Data Backups',
      'Encryption-at-Rest'
    ],
    fullDetails: [
      {
        title: 'Access Monitoring',
        description: 'Foundry logs and monitors all resource access attempts.'
      },
      {
        title: 'Data Backups',
        description:
          'Production data is regularly backed up to a separate location, and primary databases are replicated in real-time to a secondary data center with alert \
        notifications for failures.'
      },
      {
        title: 'Encryption-at-Rest',
        description: 'Sensitive customer data is encrypted when stored.'
      },
      {
        title: 'Encryption-in-Transit',
        description:
          'Secure transmission protocols encrypt confidential data over public networks.'
      },
      {
        title: 'Physical Security',
        description:
          'Access to the Foundry office is limited to authorized personnel and building staff with keys.'
      }
    ]
  },
  appSecurity: {
    icon: <Monitor />,
    summaryDetails: [
      'Credential Management',
      'Software Development Lifecycle',
      'Vulnerability & Patch Management'
    ],
    fullDetails: [
      {
        title: 'Credential Management',
        description:
          'Foundry secures secrets and credentials through a credential management system.'
      },
      {
        title: 'Software Development Lifecycle',
        description:
          'Foundry follows a formal Software Development Life Cycle (SDLC) process to oversee how software is developed, acquired, implemented, updated, including emergency changes—and \
          maintained according to technology requirements.'
      },
      {
        title: 'Vulnerability & Patch Management',
        description:
          'Foundry applies all necessary patches and software updates and keeps records of vulnerabilities.'
      },
      {
        title: 'Web Application Firewall',
        description:
          'Foundry utilizes Web Application Firewalls (WAFs) to safeguard against typical web-based threats such as SQL injection and cross-site scripting attacks.'
      }
    ]
  },
  accessControl: {
    icon: <Key />,
    summaryDetails: ['Data Access', 'Logging', 'Password Security'],
    fullDetails: [
      {
        title: 'Data Access',
        description:
          'Foundry limits privileged database access to authorized users with a business need and reviews periodically. Changes are tracked, and user access is \
          determined by job role or requires approved requests. Termination checklists ensure timely access removal within SLAs.'
      },
      {
        title: 'Logging',
        description:
          'Foundry uses a Security Information & Event Management (SIEM) tool to monitor systems and generate alerts for predefined events that could affect security objectives.'
      },
      {
        title: 'Password Security',
        description:
          'Passwords follow company policy and meet complexity requirements. System access requires valid multi-factor authentication (MFA), and encryption key access is \
          restricted to authorized personnel.'
      }
    ]
  },
  infrastructure: {
    icon: <Network />,
    summaryDetails: ['Status Monitoring', 'DDoS Protection', 'BC/DR'],
    fullDetails: [
      {
        title: 'Status Monitoring',
        description:
          'Foundry provides a public status page with uptime and incident updates: https://status.foundrydigital.com/'
      },
      {
        title: 'DDoS Protection',
        description:
          'Foundry uses anti-DDoS capabilities to ensure consistent service availability.'
      },
      {
        title: 'BC/DR',
        description:
          'Foundry has a Business Continuity and Disaster Recovery Plan to support security continuity during personnel unavailability, with regular testing.'
      },
      {
        title: 'Infrastructure Security',
        description:
          'Only authorized users with business needs have privileged production network access. Access to production data stores requires secure \
        authentication (e.g., unique SSH key). Remote access to production is limited to approved personnel via secure connections.'
      }
    ]
  },
  endpointSecurity: {
    subtitle: '',
    icon: <Laptop />,
    author: null,
    summaryDetails: [
      'Disk Encryption',
      'Endpoint Detection & Response',
      'Mobile Device Management'
    ],
    fullDetails: [
      {
        title: 'Disk Encryption',
        description: 'Foundry encrypts data on corporate laptops.'
      },
      {
        title: 'Endpoint Detection & Response',
        description:
          'Foundry deploys an EDR solution to block ransomware and malware, and to detect threats, and supports investigations.'
      },
      {
        title: 'Mobile Device Management',
        description:
          'Foundry uses a Mobile device management (MDM) system to centrally oversee company laptops.'
      },
      {
        title: 'Threat Detection',
        description:
          'Foundry uses threat detection tools to identify potential APT intrusions into the network.'
      }
    ]
  },
  networkSecurity: {
    icon: <Shield />,
    summaryDetails: [
      'Firewall',
      'IDS/IPS',
      'Security Information and Event Management'
    ],
    fullDetails: [
      {
        title: 'Firewall',
        description:
          'Foundry limits privileged firewall access to only authorized users with a business need.'
      },
      {
        title: 'IDS/IPS',
        description:
          'Foundry constantly monitors its network with an Intrusion Detection System for early security breach detection.'
      },
      {
        title: 'Security Information and Event Management',
        description:
          'Foundry uses SIEM tools to track security events in its infrastructure.'
      },
      {
        title: 'Virtual Private Cloud',
        description:
          'Foundry utilizes a Virtual Private Cloud (VPC) to ensure that all compute needs are done in a secured environment, separate from other public cloud tenants.'
      }
    ]
  },
  corporateSecurity: {
    subtitle: '',
    icon: <Building2 />,
    author: null,
    summaryDetails: [
      'Asset Management Practices',
      'Email Protection',
      'Employee Training'
    ],
    fullDetails: [
      {
        title: 'Asset Management Practices',
        description:
          'Foundry keeps a detailed record of all production system assets.'
      },
      {
        title: 'Email Protection',
        description:
          'A secure email gateway is used by Foundry to block suspicious messages.'
      },
      {
        title: 'Employee Training',
        description:
          'Employees at Foundry regularly complete security awareness training sessions.'
      },
      {
        title: 'HR Security',
        description:
          "New hires and contractors undergo background checks as part of Foundry's HR practices. Upon joining, employees sign confidentiality agreements. \
          Contractors also agree to confidentiality terms at the start of their engagement. Both groups are required to accept company policies during onboarding or upon engagement."
      },
      {
        title: 'Incident Response',
        description:
          'Foundry has established documented incident response policies for security and privacy issues, which are communicated to authorized personnel. \
          All incidents are logged, monitored, addressed, and notifications are sent to impacted parties according to these procedures. The incident response plan is tested periodically.'
      },
      {
        title: 'Internal SSO',
        description:
          'Employees must use single sign-on (SSO) to access company resources.'
      },
      {
        title: 'Penetration Testing',
        description:
          'Annual penetration tests are conducted by Foundry, followed by remediation plans and vulnerability fixes completed in accordance with SLAs.'
      },
      {
        title: 'Security Operations Center',
        description:
          'Foundry operates a dedicated, continuously monitored 24/7 security operations center that reviews logs and investigates security alerts.'
      }
    ]
  },
  legal: {
    subtitle: '',
    icon: <Scale />,
    author: null,
    summaryDetails: [
      'Privacy Policy',
      'Terms of Service',
      'Terms and Conditions'
    ],
    fullDetails: [
      {
        title: 'Privacy Policy',
        description: 'https://foundrydigital.com/privacy-policy/'
      },
      {
        title: 'Terms of Service',
        description:
          'https://pool.foundrydigital.com/app/help/terms-of-service'
      },
      {
        title: 'Terms and Conditions',
        description: 'https://foundrydigital.com/terms-and-conditions/'
      }
    ]
  },
  policies: {
    subtitle: '',
    icon: <FileText />,
    author: null,
    summaryDetails: [
      'Acceptable Use Policy',
      'Access Control Policy',
      'Anti-Malicious Software Policy'
    ],
    fullDetails: [
      {
        title: 'Acceptable Use Policy',
        description:
          'Foundry has a policy on how personnel should properly use computers, accounts, and any other company owned resources.'
      },
      {
        title: 'Access Control Policy',
        description:
          "Foundry's Access Control Policy documents the requirements for the following access control functions: adding new users, modifying users, and/or removing an existing user's access."
      },
      {
        title: 'Anti-Malicious Software Policy',
        description:
          'Foundry has a policy addressing anti-malicious software which describes the use of anti-malicious software tools on Foundry-owned equipment to reduce the impact of malware and \
           other types of unauthorized software.'
      },
      {
        title: 'Asset Management Policy',
        description:
          'Foundry has a policy addressing asset management which describes how inventory is maintained of important assets.'
      },
      {
        title: 'Backup Policy',
        description:
          "Foundry's policy addressing data backup documents requirements for backup and recovery of customer data."
      },
      {
        title: 'Business Continuity/Disaster Recovery (BC/DR) Policy',
        description:
          'Foundry has a Business Continuity Plan which defines how it will respond in the event of a serious disruption to business operations.'
      },
      {
        title: 'Data Classification Policy',
        description:
          'Foundry has a policy addressing data classification in place to help ensure that confidential data is properly secured and restricted to authorized personnel.'
      },
      {
        title: 'Data Security Policy',
        description:
          'Foundry has a policy addressing data security policy which describes safeguards around company data, such as restricted access or database encryption.'
      },
      {
        title: 'Encryption Policy',
        description:
          'Foundry has an Encryption Standard which describes requirements for encryption of laptops, servers, databases, and files.'
      },
      {
        title: 'Incident Response Policy',
        description:
          'Foundry has an Incident Response Plan which describes procedures to be taken in the event of a security incident, such as documentation requirements and communication processes.'
      },
      {
        title: 'Information Security Policy',
        description:
          "Foundry has an Information Security Policy which describes how it addresses information security for its products, services, and culture. It establishes roles and responsibilities \
           within the business pertaining to the management of information security. Foundry's information security policies and procedures are documented and reviewed periodically."
      },
      {
        title: 'Internal and External Communication Policy',
        description:
          'Foundry has a policy addressing internal and external communications, which describes the methods in which staff are to communicate with internal and external parties.'
      },
      {
        title: 'Network Security Policy',
        description:
          'Foundry has a formal systems development life cycle (SDLC) methodology in place that governs the development, acquisition, implementation, changes (including emergency changes), \
          and maintenance of information systems and related technology requirements. Foundry has a configuration management procedure in place to ensure that system configurations are \
          deployed consistently throughout the environment.'
      },
      {
        title: 'Password Policy',
        description:
          'Foundry has a password standard which describes the requirements for passwords.'
      },
      {
        title: 'Risk Assessment/Management Policy',
        description:
          'Foundry has a policy addressing risk management which describes how it identifies, documents, and manages information security risks to the business.'
      },
      {
        title: 'Software Development Lifecycle Policy',
        description:
          'Foundry has Software Development Life Cycle documents which describe the processes and procedures for developing software.'
      },
      {
        title: 'Vulnerability Management Policy',
        description:
          'Foundry has a policy addressing vulnerability management which describes how it identifies and prioritizes remediation for security vulnerabilities.'
      }
    ]
  }
};
